import React, { useState } from "react";
import { MapContainer, TileLayer, Polygon, CircleMarker, Tooltip, LayersControl, LayerGroup, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { miningSites, gradeColors } from "@/data/geoEsgData";

// Fix Leaflet default-icon path issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const SA_CENTER = [-27.5, 26.5];
const SA_ZOOM = 5;

export default function MiningMap({ onSelectSite }) {
  const [selected, setSelected] = useState(null);

  const handleSelect = (site) => {
    setSelected(site.id);
    onSelectSite?.(site);
  };

  return (
    <div className="rounded-md overflow-hidden border border-slate-200 relative" style={{ height: "100%" }}>
      <MapContainer
        center={SA_CENTER}
        zoom={SA_ZOOM}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", minHeight: 480 }}
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Topo">
            <TileLayer
              attribution='&copy; OpenTopoMap contributors'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              maxZoom={17}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Street">
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>

          <LayersControl.Overlay checked name="Site Boundaries">
            <LayerGroup>
              {miningSites.map((s) => (
                <Polygon
                  key={`b-${s.id}`}
                  positions={s.boundary}
                  pathOptions={{
                    color: s.isPrimary ? "#B45309" : "#0F172A",
                    weight: s.isPrimary ? 3 : 2,
                    fillOpacity: 0.05,
                    dashArray: s.status === "Operational" ? null : "6 6",
                  }}
                  eventHandlers={{ click: () => handleSelect(s) }}
                >
                  <Tooltip sticky direction="top" offset={[0, -10]}>
                    <div className="text-xs">
                      <div className="font-semibold text-slate-900">{s.name}</div>
                      <div className="text-slate-600">{s.region} · {s.commodity}</div>
                      <div className="text-amber-700 font-semibold">{s.status}</div>
                    </div>
                  </Tooltip>
                </Polygon>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name="Reserve Heat (grade zones)">
            <LayerGroup>
              {miningSites.flatMap((s) =>
                s.gradeZones.map((z, i) => (
                  <Polygon
                    key={`g-${s.id}-${i}`}
                    positions={z.poly}
                    pathOptions={{
                      color: gradeColors[z.grade].stroke,
                      fillColor: gradeColors[z.grade].fill,
                      fillOpacity: 0.35,
                      weight: 1,
                    }}
                  >
                    <Tooltip>
                      <div className="text-xs">
                        <span className="font-semibold">{gradeColors[z.grade].label} zone</span> · {s.region}
                      </div>
                    </Tooltip>
                  </Polygon>
                ))
              )}
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name="Drill Points">
            <LayerGroup>
              {miningSites.flatMap((s) =>
                s.drillPoints.map((d) => (
                  <CircleMarker
                    key={d.id}
                    center={[d.lat, d.lng]}
                    radius={5}
                    pathOptions={{
                      color: gradeColors[d.grade].stroke,
                      fillColor: gradeColors[d.grade].fill,
                      fillOpacity: 0.95,
                      weight: 1.5,
                    }}
                  >
                    <Tooltip>
                      <div className="text-xs">
                        <div className="font-semibold text-slate-900">{d.id}</div>
                        <div className="text-slate-700">{d.value}</div>
                        <div className="text-slate-500">{s.region}</div>
                      </div>
                    </Tooltip>
                  </CircleMarker>
                ))
              )}
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name="Site Markers">
            <LayerGroup>
              {miningSites.map((s) => (
                <Marker key={`m-${s.id}`} position={s.coords} eventHandlers={{ click: () => handleSelect(s) }}>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-semibold text-slate-900 mb-0.5">{s.name}</div>
                      <div className="text-slate-600">{s.region} · {s.commodity}</div>
                      <div className="text-amber-700 font-semibold mt-1">{s.status}</div>
                      <div className="mt-2 text-[11px] text-slate-700">
                        <div><strong>Reserves:</strong> {s.reserves}</div>
                        <div><strong>Grade:</strong> {s.grade}</div>
                        {s.activeAreaHa > 0 && <div><strong>Active area:</strong> {s.activeAreaHa} ha</div>}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>
      </MapContainer>

      {/* Grade legend overlay */}
      <div className="absolute bottom-3 left-3 bg-white/95 border border-slate-200 rounded-md px-3 py-2 z-[1000] shadow-md">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Grade zones</div>
        <div className="flex items-center gap-3 text-[11px] text-slate-700">
          {Object.entries(gradeColors).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: v.fill, border: `1px solid ${v.stroke}` }} />
              {v.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
